/**
 * The single view-visibility editor panel: one rule radio, one checklist of the
 * view pool, and an optional Member Rows toggle per row. Mounted directly by
 * settings surfaces and through `ViewVisibilityPopover` by every matrix cell,
 * so this is the one control at every visibility surface.
 * The checklist renders `own` + `org` entries until "Show all views" is on, but
 * membership never narrows: locked-id detection and the unlisted-checked warning
 * both read the FULL pool, so a narrowed list can never hide a checked view.
 * Switching from "every" to "only" seeds the checklist with every view the
 * object is currently shown in — the whole `writePool` — and emits nothing;
 * restriction happens by UNCHECKING, so a view the narrowed list never rendered
 * cannot be dropped without the user acting on it. The rule is derived from the
 * RAW stored `visibilityTypes` length — never from the filtered shown list,
 * since `['internal']` filters to `[]` yet still restricts the object.
 * Switching to "every" is the one write that bypasses `mergeVisibilityTypes`,
 * because a locked id kept there would leave the object restricted while the
 * radio claims otherwise. That write would also destroy a stored `hidden`
 * sentinel, which no surface in the app can author back, so an object carrying
 * one takes NO write here at all: every editing control goes disabled and an
 * alert says why. A failed `parameters/organization` listener disables editing
 * the same way, because the pool then degrades to the built-in defaults and a
 * complement composed against it would drop the org's real views. With
 * `memberRows` falsy the emitted object carries `visibilityTypes` alone, so no
 * dead key reaches Firestore.
 * Both entry points stay mounted always and go disabled where they would do
 * nothing — "Show all views" where the pool holds no `other` entry to reveal,
 * "Open in matrix" where the mount hosts no matrix — each with a tooltip naming
 * its own reason.
 * The highlighted row is scrolled into view by moving the checklist's OWN
 * scroll container and nothing else. `scrollIntoView` walks every scrollable
 * ancestor up to the document, and rc-trigger parks the popover mount at
 * `top: -1000vh` until its align pass runs, so on mount that walk throws the
 * whole page to the top and leaves the matrix cell the user clicked — and this
 * panel with it — off screen (SYD-2761).
 * Every row's Member Rows control is given its own `name`. Segmented renders
 * radio inputs, so rows sharing a name are ONE radio group and only the last
 * row could read as selected; antd's generated fallback name is a constant
 * under NODE_ENV=test, which collapses the rows exactly that way.
 */
import { FC, useCallback, useId, useState } from 'react';
import { Alert, Button, Checkbox, Radio, Segmented, Switch, Tag, Tooltip, Typography, theme } from 'antd';
import type { VisibilityTypeId } from '#types';
import FlexBox from '../atoms/FlexBox';
import FlexCol from '../atoms/FlexCol';
import { hiddenColumnSentinel } from './hiddenColumnSentinel';
import { useViewVisibilityPool } from './useViewVisibilityPool';
import type { MemberRowsScope, ViewVisibilityControlProps } from './ViewVisibility.types';
import { memberRowsOptions } from './memberRowsOptions';

const { Text } = Typography;

const withMemberRowsEntry = (
    list: VisibilityTypeId[] | undefined,
    id: VisibilityTypeId,
    present: boolean,
): VisibilityTypeId[] | undefined => {
    if (!present && list === undefined) return undefined;
    if (present === (list ?? []).includes(id)) return list;
    const without = (list ?? []).filter((entry) => entry !== id);
    return present ? [...without, id] : without;
};

export const ViewVisibilityControl: FC<ViewVisibilityControlProps> = ({
    value = {},
    onChange,
    formTypeId,
    objectLabel,
    objectKind,
    contextLabels,
    memberRows,
    internalMarker,
    highlightViewId,
    onOpenMatrix,
    matrixUnavailableReason,
    disabled,
    'aria-label': ariaLabel,
}) => {
    const { token } = theme.useToken();
    const memberRowsGroup = useId();
    const { pool, poolLabel, writePool, viewById, loading, error, getShownVisibilityTypes, mergeVisibilityTypes } =
        useViewVisibilityPool(formTypeId);
    const [draftShown, setDraftShown] = useState<VisibilityTypeId[] | null>(null);
    const [showAllViews, setShowAllViews] = useState(false);
    const scrollHighlightIntoView = useCallback((node: HTMLDivElement | null) => {
        const list = node?.parentElement ?? null;
        if (node === null || list === null) return;
        const above = node.getBoundingClientRect().top - list.getBoundingClientRect().top;
        const below = above + node.offsetHeight - list.clientHeight;
        if (above < 0) list.scrollTop += above;
        else if (below > 0) list.scrollTop += below;
    }, []);

    const stored = value.visibilityTypes;
    const kit = value.kitMemberHiddenTypes;
    const group = value.groupMemberHiddenTypes;

    const hiddenColumn = (stored ?? []).includes(hiddenColumnSentinel);
    const restricted = (stored?.length ?? 0) > 0;
    const rule = restricted || draftShown !== null ? 'only' : 'every';
    const shownIds = restricted ? getShownVisibilityTypes(stored) : (draftShown ?? []);
    const shownSet = new Set(shownIds);
    const poolUnavailable = Boolean(error);
    const controlsDisabled = disabled === true || loading === true;
    const editingDisabled = controlsDisabled || hiddenColumn || poolUnavailable;

    const selectableIds = new Set(pool.map((entry) => entry.view.id));
    const lockedStoredIds = Array.from(new Set(stored ?? [])).filter(
        (id) => !selectableIds.has(id) && id !== hiddenColumnSentinel,
    );
    const lockedNames = lockedStoredIds
        .map((id) => viewById.get(id)?.shortLabel)
        .filter((label): label is string => Boolean(label));
    const undefinedLockedCount = lockedStoredIds.filter((id) => !viewById.has(id)).length;
    const lockedConsequence = [
        lockedNames.join(', '),
        undefinedLockedCount > 0
            ? `${undefinedLockedCount} ${undefinedLockedCount === 1 ? 'view that is' : 'views that are'} no longer defined`
            : '',
    ]
        .filter((part) => part.length > 0)
        .join(' and ');

    const hasOtherViews = pool.some((entry) => entry.origin === 'other');
    const visibleEntries = showAllViews
        ? pool
        : pool.filter((entry) => entry.origin !== 'other' || entry.view.id === highlightViewId);
    const visibleIds = new Set(visibleEntries.map((entry) => entry.view.id));
    const unlistedCheckedCount = Array.from(shownSet).filter((id) => !visibleIds.has(id)).length;

    const shownInAnyExternalView =
        (stored ?? []).length === 0 ||
        (stored ?? []).some((id) => viewById.get(id)?.internalVisibility !== true);
    const showInternalMarkerWarning = internalMarker === true && shownInAnyExternalView;

    const emitChange = (
        visibilityTypes: VisibilityTypeId[] | undefined,
        nextKit: VisibilityTypeId[] | undefined,
        nextGroup: VisibilityTypeId[] | undefined,
    ) => {
        onChange?.(
            memberRows === true
                ? { visibilityTypes, kitMemberHiddenTypes: nextKit, groupMemberHiddenTypes: nextGroup }
                : { visibilityTypes },
        );
    };

    const handleRuleChange = (nextRule: 'every' | 'only') => {
        if (nextRule === 'only') {
            setDraftShown(writePool);
            return;
        }
        setDraftShown(null);
        emitChange([], kit, group);
    };

    const handleCheck = (id: VisibilityTypeId) => {
        const nextShown = [...shownIds, id];
        if (!restricted) setDraftShown(nextShown);
        emitChange(mergeVisibilityTypes(nextShown, stored), kit, group);
    };

    const handleUncheck = (id: VisibilityTypeId) => {
        const prune = (list: VisibilityTypeId[] | undefined) =>
            list === undefined ? undefined : list.filter((entry) => entry !== id);
        const nextShown = shownIds.filter((entry) => entry !== id);
        if (!restricted) setDraftShown(nextShown);
        emitChange(mergeVisibilityTypes(nextShown, stored), prune(kit), prune(group));
    };

    const handleMemberRowsChange = (id: VisibilityTypeId, next: MemberRowsScope) => {
        emitChange(
            stored,
            withMemberRowsEntry(kit, id, next === 'kit' || next === 'both'),
            withMemberRowsEntry(group, id, next === 'group' || next === 'both'),
        );
    };

    const memberRowsScope = (id: VisibilityTypeId): MemberRowsScope => {
        const kitOn = (kit ?? []).includes(id);
        const groupOn = (group ?? []).includes(id);
        if (kitOn && groupOn) return 'both';
        if (kitOn) return 'kit';
        if (groupOn) return 'group';
        return 'none';
    };

    const matrixButton = (
        <Button
            onClick={onOpenMatrix}
            disabled={controlsDisabled || !onOpenMatrix}
            aria-label={`Open visibility matrix: ${objectLabel}`}
        >
            Open in matrix
        </Button>
    );

    const showAllSwitch = (
        <Switch
            size="small"
            checked={showAllViews}
            onChange={setShowAllViews}
            disabled={controlsDisabled || !hasOtherViews}
            aria-label={`Show all views: ${objectLabel}`}
        />
    );

    return (
        <FlexCol gap={10} aria-label={ariaLabel}>
            <FlexCol gap={0}>
                <FlexBox gap={5} justifyStart noGrow>
                    <Text strong>{objectLabel}</Text>
                    <Text type="secondary">{objectKind}</Text>
                </FlexBox>
                <Text type="secondary">{[...(contextLabels ?? []), poolLabel].join(' · ')}</Text>
            </FlexCol>

            <Radio.Group
                value={rule}
                onChange={(event) => handleRuleChange(event.target.value as 'every' | 'only')}
                disabled={editingDisabled}
            >
                <FlexCol gap={5}>
                    <Radio value="every" aria-label={`Shown in every view: ${objectLabel}`}>
                        <FlexCol gap={0}>
                            <Text>Shown in every view</Text>
                            <Text type="secondary">Including views added later</Text>
                            {lockedConsequence.length > 0 && (
                                <Text type="secondary">Also clears {lockedConsequence}</Text>
                            )}
                        </FlexCol>
                    </Radio>
                    <Radio value="only" aria-label={`Shown only in these views: ${objectLabel}`}>
                        <Text>Shown only in these views</Text>
                    </Radio>
                </FlexCol>
            </Radio.Group>

            <FlexCol gap={5}>
                {unlistedCheckedCount > 0 && (
                    <Text type="warning">
                        {`${unlistedCheckedCount} checked ${unlistedCheckedCount === 1 ? 'view' : 'views'} from other form types ${unlistedCheckedCount === 1 ? 'is' : 'are'} not listed here.`}
                    </Text>
                )}
                <FlexBox gap={5} justifyStart noGrow>
                    {hasOtherViews ? (
                        showAllSwitch
                    ) : (
                        <Tooltip title="Every view the organization defines is already listed.">
                            <span>{showAllSwitch}</span>
                        </Tooltip>
                    )}
                    <Text type="secondary">Show all views</Text>
                </FlexBox>
            </FlexCol>

            <FlexCol gap={5} style={{ maxHeight: token.controlHeight * 8, overflowY: 'auto' }}>
                {visibleEntries.map(({ view, origin }) => {
                    const isLastChecked =
                        rule === 'only'
                        && lockedStoredIds.length === 0
                        && shownSet.size === 1
                        && shownSet.has(view.id);
                    const highlighted = view.id === highlightViewId;
                    return (
                        <FlexBox
                            key={view.id}
                            gap={10}
                            ref={highlighted ? scrollHighlightIntoView : undefined}
                            style={
                                highlighted
                                    ? {
                                        backgroundColor: token.controlItemBgActive,
                                        borderRadius: token.borderRadiusSM,
                                        padding: `0 ${token.paddingXXS}px`,
                                    }
                                    : undefined
                            }
                        >
                            <FlexBox gap={5} justifyStart noGrow>
                                <Tooltip
                                    title={
                                        isLastChecked
                                            ? 'This is the last selected view. Switch to "Shown in every view" instead of unchecking it.'
                                            : (view.longDescription ?? '')
                                    }
                                >
                                    <Checkbox
                                        checked={rule === 'only' && shownSet.has(view.id)}
                                        disabled={editingDisabled || rule === 'every' || isLastChecked}
                                        onChange={(event) =>
                                            event.target.checked ? handleCheck(view.id) : handleUncheck(view.id)
                                        }
                                        aria-label={`Shown in ${view.shortLabel}: ${objectLabel}`}
                                    >
                                        <Text>{view.shortLabel}</Text>
                                    </Checkbox>
                                </Tooltip>
                                {origin === 'own' && <Tag color="blue">Form</Tag>}
                                {view.isPdfExport === true && <Tag color="blue">pdf</Tag>}
                                {view.internalVisibility === true && <Tag>internal</Tag>}
                            </FlexBox>
                            {memberRows === true && (
                                <Segmented<MemberRowsScope>
                                    size="small"
                                    name={`${memberRowsGroup}-${view.id}`}
                                    value={memberRowsScope(view.id)}
                                    onChange={(next) => handleMemberRowsChange(view.id, next)}
                                    options={memberRowsOptions}
                                    disabled={editingDisabled}
                                    aria-label={`Kit/Group Member Rows in ${view.shortLabel}: ${objectLabel}`}
                                />
                            )}
                        </FlexBox>
                    );
                })}
            </FlexCol>

            {poolUnavailable && (
                <Alert
                    type="error"
                    showIcon
                    title="The organization's view list could not be loaded, so only the built-in views are listed. Editing stays disabled until it loads."
                />
            )}

            {hiddenColumn && (
                <Alert
                    type="warning"
                    showIcon
                    title={`This ${objectKind} is hidden outright: it never appears in any view. Nothing here can author or clear that setting.`}
                />
            )}

            {showInternalMarkerWarning && (
                <Alert
                    type="warning"
                    showIcon
                    title={`This ${objectKind} is marked internal only, yet it is shown in an external view.`}
                />
            )}

            <FlexBox gap={10}>
                <Text type="secondary">Internal view always shows this</Text>
                {onOpenMatrix ? (
                    matrixButton
                ) : (
                    <Tooltip title={matrixUnavailableReason ?? ''}>
                        <span>{matrixButton}</span>
                    </Tooltip>
                )}
            </FlexBox>
        </FlexCol>
    );
};
